/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { forwardRef } from 'react';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

import Link from '../navigation/Link';
import { PageBackgroundButton } from './PageBackground';

const Table = ({ children }) => {
	return (
		<table
			css={css`
				width: 100%;
				overflow: auto;
				border-collapse: collapse;
			`}
		>
			<tbody>{children}</tbody>
		</table>
	);
};

const nodeChildsToReact = (children) => {
	return children.map((child, i) => {
		switch (child.type) {
			case 'tag':
				return React.createElement(child.name, { key: i, ...child.attribs }, nodeChildsToReact(child.children));
			case 'text':
				return child.data;
			default:
				return child;
		}
	});
};

const parser = new Parser();
const isValidNode = () => true;
const processNodeDefinitions = new ProcessNodeDefinitions(React);
const processingInstructions = [
	{
		// page background link
		replaceChildren: false,
		shouldProcessNode: function (node) {
			if (!node.attribs?.class) return false;
			return node.attribs.class.indexOf('rte-pagebackground-link') !== -1;
		},
		processNode: function (node, children) {
			return (
				<PageBackgroundButton className={node.attribs?.class}>
					{node.children && nodeChildsToReact(children)}
				</PageBackgroundButton>
			);
		},
	},
	{
		// Custom <a> processing
		replaceChildren: false,
		shouldProcessNode: function (node) {
			return (
				node.name === 'a' &&
				node.attribs.href &&
				!node.attribs.target &&
				node.attribs.href.indexOf('://') === -1 &&
				node.attribs.href.indexOf('mailto:') !== 0 &&
				node.attribs.href.indexOf('tel:') !== 0 &&
				node.attribs.href.indexOf('/assets/') === -1
			);
		},
		processNode: function (node, children) {
			return (
				<Link href={node.attribs.href} className={node.attribs.class}>
					{node.children && nodeChildsToReact(children)}
				</Link>
			);
		},
	},
	{
		// <table>
		shouldProcessNode: function (node) {
			return node.name === 'table';
		},
		processNode: function (node, children, index) {
			// let's eliminate white spaces from rte editor
			const pureChildren = children.filter((_node) => {
				return typeof _node === 'object';
			});
			return <Table key={index}>{nodeChildsToReact(pureChildren)}</Table>;
		},
	},
	{
		// <tr>
		shouldProcessNode: function (node) {
			return node.name === 'tr';
		},
		processNode: function (node, children, index) {
			// let's eliminate white spaces from rte editor
			const pureChildren = children.filter((_node) => {
				return typeof _node === 'object';
			});
			return <tr key={index}>{nodeChildsToReact(pureChildren)}</tr>;
		},
	},
	{
		// Anything else
		shouldProcessNode: function () {
			return true;
		},
		processNode: processNodeDefinitions.processDefaultNode,
	},
];

// TODO: RTE styles
const wrap = ({ theme }) => css`
	> * {
		${theme.typography.getStyle({ theme })};
	}

	strong {
		${theme.font.default.weight.regular};
	}

	& .text-left {
		text-align: left;
	}

	& .text-right {
		text-align: right;
	}

	& .text-center {
		text-align: center;
	}

	& .text-justify {
		text-align: justify;
	}

	& > {
		h1,
		h2,
		h3,
		h4,
		h5,
		h6,
		p,
		blockquote,
		ul {
			font-weight: 400;

			&:first-of-type {
				margin-top: 0;
			}
			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	& ul {
		& li {
			margin-bottom: 0.8em;
		}
	}

	& .rte-text-uppercase {
		text-transform: uppercase;
	}

	& .rte-nowrap {
		white-space: nowrap;
	}

	& .rte-font-big {
		${theme.typography.getStyle({ theme, size: 'l' })};
	}

	& .rte-font-regular {
		${theme.typography.getStyle({ theme, size: 'm' })};
	}

	& .rte-font-small {
		${theme.typography.getStyle({ theme, size: 's' })};
	}

	& .rte-button {
		${theme.button({ theme })};
	}
`;

const RichText = ({ content, ...props }, ref) => {
	return (
		<div ref={ref} css={(theme) => wrap({ theme })} {...props}>
			{parser.parseWithInstructions(content, isValidNode, processingInstructions)}
		</div>
	);
};

export default React.memo(forwardRef(RichText));
